import { Injectable } from '@angular/core';
import {
    ActivatedRouteSnapshot,
    CanActivate,
    Router,
    RouterStateSnapshot,
    UrlTree,
} from '@angular/router';
import { Store } from '@ngrx/store';
import { AuthService } from '../../auth/services/auth.service';
import { environment } from '../../../environments/environment';
import { IAppState } from '../../store/interface/app.state.interface';
import { UserTypes } from '../../shared/enum/user-types.enum';
import { IUser } from '../../user/interfaces/user.interface';
import { UserService } from '../../user/services/user.service';

/*=====================================
  Guards self-serve signup, both halves of it.

  Named for the FUNNEL, not for one screen. Only the first half is signup in
  the narrow sense: /auth/signup creates an ACCOUNT, /onboarding gives that
  account its first STORE. The API draws the same line - POST /auth/signup/account
  against POST /tenants/onboarding - and calls the whole thing self-signup,
  which is also what tenants.signup_source records.

  The flow is split across two prefixes because its two halves want opposite
  callers. /auth/signup belongs to a stranger, and PublicGuard - which every
  route in AuthModule already carries - turns away anyone holding a token, so
  there is nothing left for this guard to decide there. /onboarding is the
  reverse: the shop is created for whoever holds the token, so those screens
  are outside /auth precisely so PublicGuard does not reach them, and this
  guard is what asks for the token instead.

  Google hands a merchant back to /auth/success rather than to anything here,
  so nothing under /onboarding ever arrives without a token in place already.

  Closed in production for now, which is the one rule both halves share and
  the reason a single guard still covers them. The API's own SignupEnabledGuard
  closes the matching routes there; this only spares the browser the trip.

  The wizard steps also refuse at the shop ceiling, sending the merchant to
  the refusal screen rather than letting them fill in a form that
  POST /tenants/onboarding is going to turn down. A PLATFORM account is
  refused outright for the same reason and one step earlier: it may not own a
  store at all, so no step of the wizard can end anywhere but a 403.
======================================*/
@Injectable()
export class SignupGuard implements CanActivate {
    private readonly wizardRoutes = ['/onboarding/shop', '/onboarding/theme'];
    private readonly ceilingRoute = '/onboarding/limit';

    constructor(
        private router: Router,
        private authService: AuthService,
        private store: Store<IAppState>,
        private userService: UserService,
    ) {}

    async canActivate(
        _route: ActivatedRouteSnapshot,
        state: RouterStateSnapshot,
    ): Promise<boolean | UrlTree> {
        if (environment.production) {
            return this.router.parseUrl('/');
        }

        const path = state.url.split('?')[0];
        if (path.startsWith('/auth/signup')) {
            return true;
        }

        if (!localStorage.getItem('auth_token')) {
            return this.router.parseUrl('/auth/signup');
        }

        if (await this.isTTUserAccount()) {
            return this.router.parseUrl('/');
        }

        if (this.wizardRoutes.includes(path) && (await this.isAtCeiling())) {
            return this.router.parseUrl(this.ceilingRoute);
        }

        if (path === this.ceilingRoute && !(await this.isAtCeiling())) {
            return this.router.parseUrl(this.wizardRoutes[0]);
        }

        return true;
    }

    /*=====================================
      Whether the signed-in account belongs to the platform rather than to a
      store.

      Checked BEFORE the ceiling, because a platform account has no ceiling to
      be under - POST /tenants/onboarding refuses it outright - and bouncing it
      to /onboarding/limit would explain a limit it never reached.

      /onboarding is deliberately outside the AuthGuard'd tree, so the account
      may not be in the store yet when somebody lands on these URLs directly.
      The read is fetched in that case rather than assumed, and a failed fetch
      is treated as NOT platform: the create call refuses on its own check, so
      the worst case is a wasted form, not a merchant locked out of opening a
      shop.
      @return whether the wizard should refuse this account
    ======================================*/
    private async isTTUserAccount(): Promise<boolean> {
        let account = this.readAccount();

        if (!account) {
            try {
                await this.userService.fetchLoggedInUser();
            } catch {
                return false;
            }
            account = this.readAccount();
        }

        return account?.userType?.key === UserTypes.TT_USER;
    }

    /*=====================================
      The account as the store currently holds it. The select is synchronous,
      so subscribing and releasing it immediately reads the current value
      without leaving a subscription behind - the same read AuthGuard does.
      @return the signed-in account, or null
    ======================================*/
    private readAccount(): IUser | null {
        let account: IUser | null = null;
        this.store
            .select((state) => state.user.loggedInUser)
            .subscribe((value) => (account = value))
            .unsubscribe();

        return account as IUser | null;
    }

    /*=====================================
      Whether this account already owns as many stores as it may.

      A failed read is treated as NOT at the ceiling: the wizard's own create
      call refuses on the server's count, so the worst case is a merchant
      filling in a form and being turned down at the end - which is better
      than a network blip locking somebody out of opening a shop.
      @return whether the wizard should refuse
    ======================================*/
    private async isAtCeiling(): Promise<boolean> {
        try {
            const { ownedCount, limit } = await this.authService.getSwitchableTenants();
            return limit > 0 && ownedCount >= limit;
        } catch {
            return false;
        }
    }
}
